import { apiClient } from './client'
import type { ApiResponse } from './client'

// ============================================================================
// WORKFLOW SETTINGS TYPES
// ============================================================================

/** The level at which workflow settings are configured */
export type WorkflowSettingsLevel = 'workspace' | 'campaign' | 'web'

/**
 * User information for workflow settings (reviewer or internal team member)
 */
export interface WorkflowSettingsUserDto {
  /** The unique identifier for the user profile */
  Id: string
  /** The user's first name */
  FirstName: string
  /** The user's last name */
  LastName: string
  /** The URL to the user's avatar image */
  AvatarUrl: string
}

/**
 * File restriction settings for asset uploads
 */
export interface AssetFileRestrictionsDto {
  /** Whether image files are allowed */
  AllowImages: boolean
  /** Whether document files are allowed */
  AllowDocuments: boolean
  /** Whether video files are allowed */
  AllowVideos: boolean
  /** Whether archive files (zip, etc.) are allowed */
  AllowArchives: boolean
  /** Maximum file size in bytes (default: 524,288,000 = 500MB) */
  MaxFileSizeBytes: number
  /** List of specific file extensions allowed (optional) */
  AllowedExtensions?: string[]
}

/**
 * Generalized workflow settings view model (returned by GET endpoint at any level).
 * Used at workspace, campaign, and web levels.
 */
export interface AssetWorkflowSettingsViewDto {
  /** Unique identifier */
  Id: string
  /** ETag for optimistic concurrency */
  ETag: string
  /** Workspace ID this belongs to */
  WorkspaceId: string
  /** The level these settings belong to: "Workspace", "Campaign", or "Web" */
  SettingsLevel: string
  /** Campaign ID (absent at workspace level) */
  CampaignId?: string
  /** Web ID (absent at workspace and campaign levels) */
  WebId?: string
  /** Whether this level inherits settings from its parent */
  InheritFromParent: boolean
  /** List of default reviewer users */
  DefaultReviewerUsers: WorkflowSettingsUserDto[]
  /** List of internal team users */
  InternalTeamUsers: WorkflowSettingsUserDto[]
  /** Default status description when assets are first uploaded */
  DefaultStatusOnUpload: string
  /** Whether sign-off requires explicit confirmation */
  RequireSignOffConfirmation: boolean
  /** Whether comments are allowed after sign-off/approval */
  AllowCommentsAfterSignOff: boolean
  /** Whether only internal users can reopen approved assets */
  InternalOnlyReopen: boolean
  /** File type and size restrictions */
  FileRestrictions: AssetFileRestrictionsDto
  /** Timestamp when settings were created */
  CreatedAt: string
  /** User who created these settings */
  CreatedBy: string
  /** Timestamp when settings were last updated */
  UpdatedAt?: string
  /** User ID who last updated these settings */
  UpdatedBy?: string
}

/**
 * Generalized workflow settings update model (sent to PUT endpoint at any level).
 */
export interface AssetWorkflowSettingsDto {
  /** Workspace ID this belongs to */
  WorkspaceId: string
  /** Campaign ID (optional, set for campaign and web levels) */
  CampaignId?: string
  /** Web ID (optional, set for web level only) */
  WebId?: string
  /** Whether this level inherits settings from its parent */
  InheritFromParent: boolean
  /** List of default reviewer user IDs */
  DefaultReviewerUserIds: string[]
  /** List of internal team user IDs */
  InternalTeamUserIds: string[]
  /** Default status when assets are first uploaded (nullable when inheriting) */
  DefaultStatusOnUpload?: string
  /** Whether sign-off requires explicit confirmation */
  RequireSignOffConfirmation: boolean
  /** Whether comments are allowed after sign-off/approval */
  AllowCommentsAfterSignOff: boolean
  /** Whether only internal users can reopen approved assets */
  InternalOnlyReopen: boolean
  /** File type and size restrictions */
  FileRestrictions: AssetFileRestrictionsDto
  /** ETag for optimistic concurrency (optional for create, required for update) */
  ETag?: string
}

/** @deprecated Use AssetWorkflowSettingsViewDto instead */
export type WebAssetWorkflowSettingsViewDto = AssetWorkflowSettingsViewDto

/** @deprecated Use AssetWorkflowSettingsDto instead */
export type WebAssetWorkflowSettingsDto = AssetWorkflowSettingsDto

/**
 * Eligible user search result (for type-ahead)
 */
export interface EligibleUserDto {
  /** User ID */
  Id: string
  /** First name */
  FirstName: string
  /** Last name */
  LastName: string
  /** Avatar URL */
  AvatarUrl: string
  /** Email address (for additional context) */
  Email?: string
}

// ============================================================================
// API CLIENT
// ============================================================================

export const workflowSettingsApi = {
  // ---------- WORKSPACE LEVEL ----------

  /** Get workflow settings for a workspace */
  getWorkspaceSettings: (
    clientCompanyId: string,
    workspaceId: string
  ): Promise<ApiResponse<AssetWorkflowSettingsViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/WorkflowSettings`),

  /** Create or update workflow settings for a workspace */
  updateWorkspaceSettings: (
    clientCompanyId: string,
    workspaceId: string,
    settings: AssetWorkflowSettingsDto
  ): Promise<ApiResponse<AssetWorkflowSettingsViewDto>> =>
    apiClient.put(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/WorkflowSettings`, settings),

  // ---------- CAMPAIGN LEVEL ----------

  /** Get workflow settings for a campaign */
  getCampaignSettings: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string
  ): Promise<ApiResponse<AssetWorkflowSettingsViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/WorkflowSettings`),

  /** Create or update workflow settings for a campaign */
  updateCampaignSettings: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    settings: AssetWorkflowSettingsDto
  ): Promise<ApiResponse<AssetWorkflowSettingsViewDto>> =>
    apiClient.put(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/WorkflowSettings`, settings),

  // ---------- WEB LEVEL ----------

  /** Get workflow settings for a specific web */
  getWebSettings: (
    clientCompanyId: string,
    workspaceId: string,
    webId: string
  ): Promise<ApiResponse<AssetWorkflowSettingsViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Webs/${webId}/WorkflowSettings`),

  /** Create or update workflow settings for a web */
  updateWebSettings: (
    clientCompanyId: string,
    workspaceId: string,
    webId: string,
    settings: AssetWorkflowSettingsDto
  ): Promise<ApiResponse<AssetWorkflowSettingsViewDto>> =>
    apiClient.put(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Webs/${webId}/WorkflowSettings`, settings),

  // ---------- EFFECTIVE (RESOLVED) SETTINGS ----------

  /** Get the effective/resolved workflow settings for a web (walks up the inheritance chain) */
  getEffectiveWebSettings: (
    clientCompanyId: string,
    workspaceId: string,
    campaignId: string,
    webId: string
  ): Promise<ApiResponse<AssetWorkflowSettingsViewDto>> =>
    apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/WorkflowSettings/Effective`),

  // ---------- USER SEARCH ----------

  /**
   * Search for eligible users (for reviewer or internal team assignment)
   * @param workspaceId - Workspace ID
   * @param clientCompanyId - Client company ID
   * @param searchTerm - Search term (name, email, etc.)
   * @param userType - Type of user ("reviewer" or "internal")
   */
  searchEligibleUsers: (
    workspaceId: string,
    clientCompanyId: string,
    searchTerm: string,
    userType: 'reviewer' | 'internal'
  ): Promise<ApiResponse<EligibleUserDto[]>> =>
    apiClient.get(
      `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/EligibleUsers?searchTerm=${encodeURIComponent(searchTerm)}&userType=${encodeURIComponent(userType)}`
    ),
}

// ============================================================================
// CLIENT-SIDE SETTINGS RESOLUTION
// ============================================================================

/**
 * Resolves effective settings for a web by walking up the inheritance chain.
 * Temporary fallback until the backend /Effective endpoint is available.
 * Tries: Web → Campaign → Workspace
 */
export async function resolveEffectiveSettings(
  clientCompanyId: string,
  workspaceId: string,
  campaignId: string,
  webId: string
): Promise<ApiResponse<AssetWorkflowSettingsViewDto>> {
  // Try the backend Effective endpoint first
  const effectiveRes = await workflowSettingsApi.getEffectiveWebSettings(clientCompanyId, workspaceId, campaignId, webId)
  if (effectiveRes.data) return effectiveRes

  // Fallback: walk the chain client-side
  const webRes = await workflowSettingsApi.getWebSettings(clientCompanyId, workspaceId, webId)
  if (webRes.data && !webRes.data.InheritFromParent) return webRes

  const campaignRes = await workflowSettingsApi.getCampaignSettings(clientCompanyId, workspaceId, campaignId)
  if (campaignRes.data && !campaignRes.data.InheritFromParent) return campaignRes

  return workflowSettingsApi.getWorkspaceSettings(clientCompanyId, workspaceId)
}
